import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useCompanySearch, type CompanyType } from "~/services/customer/home";
import { useDebounce } from "^/common/useDebounce";
import { IconSearch } from "@/Icon/Search";
import { IconFoot } from "@/Icon/Foot";
import { IconMungSmall } from "@/Icon/MungSmall";
import { LocationPermissionDialog } from "@/LocationPermissionDialog";
import {
  COMPANY_CATEGORIES,
  providesCategory,
} from "%/companyCategories";
import {
  requestDeviceLocation,
  type DeviceLocationError,
} from "%/nativeLocation";

interface SearchParams {
  q?: string;
}

export const Route = createFileRoute("/customer/layout/search")({
  component: SearchPage,
  validateSearch: (search: Record<string, unknown>): SearchParams => ({
    q: search.q as string | undefined,
  }),
});

const PAGE_SIZE = 20;

function highlight(text: string, query: string) {
  const q = query.trim();
  if (!q) return <>{text}</>;
  const lower = text.toLowerCase();
  const qLower = q.toLowerCase();
  const nodes: React.ReactNode[] = [];
  let i = 0;
  while (i < text.length) {
    const idx = lower.indexOf(qLower, i);
    if (idx === -1) {
      nodes.push(text.slice(i));
      break;
    }
    if (idx > i) nodes.push(text.slice(i, idx));
    nodes.push(
      <span key={idx} className="text-yellow">
        {text.slice(idx, idx + q.length)}
      </span>
    );
    i = idx + q.length;
  }
  return <>{nodes}</>;
}

function SearchPage() {
  const { history } = useRouter();
  const { q: initialQ } = Route.useSearch();
  const [query, setQuery] = useState(initialQ ?? "");
  const debouncedQuery = useDebounce(query, 300);
  const [categoryValue, setCategoryValue] = useState<string | null>(null);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  // 거리순 정렬: 켜는 순간 위치 권한을 요청하고, 좌표를 얻어야만 활성화된다
  const [distanceOn, setDistanceOn] = useState(false);
  const [coords, setCoords] = useState<{ lat: number; lng: number } | null>(
    null
  );
  const [locating, setLocating] = useState(false);
  const [locationError, setLocationError] =
    useState<DeviceLocationError | null>(null);

  const keyword = debouncedQuery.trim();

  // 검색 조건이 바뀌면 페이지 크기 리셋
  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [keyword, categoryValue, distanceOn]);

  const toggleDistance = async () => {
    if (locating) return;
    if (distanceOn) {
      setDistanceOn(false);
      return;
    }
    setLocating(true);
    try {
      const result = await requestDeviceLocation({
        forceRefresh: true,
        interactive: true,
      });
      const location = result.location;
      if (location) {
        setCoords({ lat: location.lat, lng: location.lng });
        setDistanceOn(true);
      } else {
        setLocationError(result.error);
      }
    } finally {
      setLocating(false);
    }
  };

  // 더보기 시 size를 늘려 전체를 다시 받는다 (병합 정렬 결과의 페이지 경계가 안정적)
  const { data: companyList = [] } = useCompanySearch(
    {
      keyword,
      category: categoryValue,
      sort: distanceOn && coords ? "DISTANCE" : null,
      latitude: distanceOn && coords ? coords.lat : null,
      longitude: distanceOn && coords ? coords.lng : null,
      page: 0,
      size: visibleCount,
    },
    !!keyword
  );

  const hasMore = companyList.length === visibleCount;

  const goDetail = (c: CompanyType) => {
    const params = new URLSearchParams({
      companySeq: c.companySeq.toString(),
    });
    if (c.masterYn) params.append("masterYn", c.masterYn);
    history.push(`/customer/layout/home-serach-detail?${params.toString()}`);
  };

  return (
    <div className="flex-auto bg-[#F5F5F5] px-4 pt-4">
      <LocationPermissionDialog
        error={locationError}
        onClose={() => setLocationError(null)}
      />
      <div className="relative mb-3">
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="매장 이름으로 검색"
          autoFocus
          className="h-11 w-full rounded-full bg-white px-4 pr-11 text-sm outline-none placeholder:text-gray"
        />
        <span className="absolute right-3 top-1/2 -translate-y-1/2">
          <IconSearch />
        </span>
      </div>

      <div className="-mx-4 mb-3 overflow-x-auto">
        <div className="flex items-center gap-2 px-4">
          <button
            type="button"
            onClick={toggleDistance}
            className={`h-8 shrink-0 rounded-full border px-3 text-xs font-bold ${
              distanceOn
                ? "border-yellow bg-white text-yellow"
                : "border-[#E5E5E5] bg-white text-gray"
            }`}
          >
            {locating ? "위치 확인 중..." : "거리순"}
          </button>
          <span className="h-4 w-px shrink-0 bg-[#E5E5E5]" />
          {COMPANY_CATEGORIES.map((cat) => {
            const active = categoryValue === cat.value;
            return (
              <button
                key={cat.label}
                type="button"
                onClick={() => setCategoryValue(cat.value)}
                className={`flex h-8 shrink-0 items-center gap-1 rounded-full border px-3 text-xs font-bold bg-white ${
                  active
                    ? "border-yellow text-yellow"
                    : "border-[#E5E5E5] text-gray"
                }`}
              >
                <span
                  className="inline-block h-1.5 w-1.5 rounded-full"
                  style={{ backgroundColor: cat.color }}
                />
                {cat.label}
              </button>
            );
          })}
        </div>
      </div>

      {!keyword ? (
        <div className="flex flex-col items-center justify-center pt-20">
          <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-yellow">
            <span className="text-white">
              <IconFoot color="#FFFFFF" />
            </span>
          </div>
          <div className="text-sm font-bold text-black">
            검색어를 입력하세요
          </div>
        </div>
      ) : (
        <>
          <div className="mb-2 text-sm font-bold">
            {companyList.length}
            {hasMore ? "+" : ""} 개
          </div>

          {companyList.length === 0 ? (
            <div className="rounded-xl bg-white p-6 text-center text-sm text-gray">
              검색 결과가 없습니다.
            </div>
          ) : (
            <>
              <div className="flex flex-col gap-2">
                {companyList.map((c: CompanyType) => (
                  <button
                    key={`${c.masterYn ?? "N"}-${c.companySeq}`}
                    type="button"
                    onClick={() => goDetail(c)}
                    className="flex flex-col gap-1 rounded-xl bg-white p-4 text-left"
                  >
                    <div className="flex flex-wrap items-center gap-1">
                      {c.activeYn === "Y" && (
                        <span className="shrink-0" aria-label="가맹점">
                          <IconMungSmall size={14} />
                        </span>
                      )}
                      <span className="truncate text-base font-bold">
                        {highlight(c.companyName, query)}
                      </span>
                      {COMPANY_CATEGORIES.filter((cat) => cat.value).map(
                        (cat) => {
                          if (!providesCategory(c, cat)) return null;
                          return (
                            <span
                              key={cat.label}
                              title={cat.label}
                              className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-[10px] font-bold leading-none text-white"
                              style={{ backgroundColor: cat.color }}
                            >
                              {cat.label.slice(0, 1)}
                            </span>
                          );
                        }
                      )}
                    </div>
                    <div className="truncate text-xs text-gray">
                      {c.companyAddr}
                    </div>
                  </button>
                ))}
              </div>

              {hasMore && (
                <button
                  type="button"
                  onClick={() => setVisibleCount((v) => v + PAGE_SIZE)}
                  className="mt-4 h-12 w-full rounded-xl bg-[#FFF3E5] text-sm font-bold text-yellow"
                >
                  더보기
                </button>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}
